import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AiPromptTemplate } from './types';
import {
  createTesterPrompt,
  getProductionPromptContent,
  getTesterPromptContent,
  saveTesterPromptContent,
} from './api';

interface PromptPickerProps {
  label: string;
  generatorKey: string;
  stepKey: string;
  prompts: AiPromptTemplate[];
  selectedId: number | null;
  blobFilename: string;
  onSelect: (prompt: AiPromptTemplate | null) => void;
  onContentChange?: (content: string) => void;
  onPromptsChanged: () => Promise<void> | void;
  disabled?: boolean;
}

const PromptPicker: React.FC<PromptPickerProps> = ({
  label,
  generatorKey,
  stepKey,
  prompts,
  selectedId,
  blobFilename,
  onSelect,
  onContentChange,
  onPromptsChanged,
  disabled,
}) => {
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = prompts.find((p) =>
    selectedId ? p.id === selectedId : Boolean(blobFilename) && p.blob_filename === blobFilename
  );
  const selectedPromptId = selected?.id;
  const selectedPromptName = selected?.name;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selected && !blobFilename) {
        setContent('');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (selectedPromptId) {
          const data = await getTesterPromptContent(selectedPromptId);
          if (!cancelled) {
            setContent(data.content);
            setName(selectedPromptName || '');
          }
        } else if (blobFilename) {
          const data = await getProductionPromptContent(blobFilename);
          if (!cancelled) {
            setContent(data.content);
            setName(selected?.name || blobFilename);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load prompt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [selectedPromptId, selectedPromptName, blobFilename]);

  useEffect(() => {
    onContentChange?.(content);
  }, [content, onContentChange]);

  const handleSelect = (value: string) => {
    if (!value) {
      onSelect(null);
      setContent('');
      setName('');
      return;
    }
    const [kind, rest] = value.split(':', 2);
    if (kind === 'id') {
      const prompt = prompts.find((p) => String(p.id) === rest) || null;
      onSelect(prompt);
      return;
    }
    const prompt = prompts.find((p) => p.blob_filename === rest) || {
      id: null,
      generator_key: generatorKey as AiPromptTemplate['generator_key'],
      step_key: stepKey,
      name: rest,
      blob_container: 'prompts',
      blob_filename: rest,
      is_test: false,
      notes: '',
      created_by: null,
      created_by_email: null,
      created_at: null,
      updated_at: null,
    };
    onSelect(prompt);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (selected?.id && selected.is_test) {
        await saveTesterPromptContent(selected.id, content, name || selected.name);
        await onPromptsChanged();
      } else {
        const created = await createTesterPrompt({
          generator_key: generatorKey,
          step_key: stepKey,
          name: name.trim() || 'Untitled prompt',
          content,
        });
        await onPromptsChanged();
        onSelect(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await createTesterPrompt({
        generator_key: generatorKey,
        step_key: stepKey,
        name: `${(selected?.name || name || 'prompt').trim()} copy`,
        content,
      });
      await onPromptsChanged();
      onSelect(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not duplicate prompt');
    } finally {
      setSaving(false);
    }
  };

  const selectValue = selected?.id
    ? `id:${selected.id}`
    : selected?.blob_filename
      ? `blob:${selected.blob_filename}`
      : '';

  return (
    <div className="space-y-2">
      <label className="block text-[12px] font-medium text-[#626262]">{label}</label>
      <select
        value={selectValue}
        onChange={(e) => handleSelect(e.target.value)}
        disabled={disabled}
        className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
      >
        <option value="">Write a new prompt</option>
        {prompts.map((p) => {
          const value = p.id ? `id:${p.id}` : `blob:${p.blob_filename}`;
          const suffix = p.is_test ? '' : ' (production)';
          return (
            <option key={value} value={value}>
              {p.name}
              {suffix}
            </option>
          );
        })}
      </select>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Prompt name"
        disabled={disabled}
        className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={10}
        disabled={disabled || loading}
        className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm font-mono min-h-[180px]"
        placeholder="Prompt text. Use {{OPPORTUNITY_SPACE}}, {{COUNTRY}}, {{MARKETS}}."
      />
      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-[#8A8A8A]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading prompt…
        </div>
      )}
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={disabled || saving || !content.trim()}
          className="h-9 px-3 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
        >
          {selected?.id && selected.is_test ? 'Save' : 'Save as new'}
        </button>
        <button
          type="button"
          onClick={() => void handleDuplicate()}
          disabled={disabled || saving || !content.trim()}
          className="h-9 px-3 rounded-md border border-[#D1D1D1] text-sm disabled:opacity-50"
        >
          Duplicate
        </button>
      </div>
    </div>
  );
};

export default PromptPicker;
